import PropTypes from 'prop-types';

import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { DynoFavouriteSection } from 'components/dyno-template';
import { getMaintenanceTransfers } from 'middleware/actions/favourite/transfer';

import { get } from 'utils/lodash';
import { getSearchableFavouriteAccounts } from 'utils/transfer/getter';
import { formatTransferFavourites } from 'utils/favourite/formatter';
import { formatOwnTransferAccounts } from 'utils/transfer/formatter';

const TransferFavouriteContainer = ({
  userId,
  isFormOpen,
  searchValue,
  onAddFavourite,
  onKeywordChange,
  onEditFavourite,
  transactionType,
  receiverAccounts,
  favouriteAccount,
  onDeleteFavourite,
  onTransferFromFavourite,
  onOwnAccountChange,
  senderAccount,
  onFavouriteTypeChange,
  isOwnAccountFavouriteDisplay,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const favouriteAccounts = get(favouriteAccount, 'favouriteAccounts', []);
  const searchableAccounts = getSearchableFavouriteAccounts(favouriteAccounts);
  const label = isOwnAccountFavouriteDisplay ? 'transaction.transfer.ownAccount.label' : 'transaction.favourite.label';
  // only used for own account transfer
  const senderAccountNumber = get(senderAccount, 'accountNumber', '');
  const senderCurrency = get(senderAccount, 'currency', '');

  const handleAddNewFavourite = () => {
    // get transfer types (only for new fav from main transfer page)
    dispatch(getMaintenanceTransfers({ userId }));
    // lastly call handleOnAddFavourite() in index to open modal
    onAddFavourite(true);
  };

  const handleTransferFromFavourite = index => {
    const selectedFavourite = favouriteAccounts.find(account => account.index === index);
    const transferType = get(selectedFavourite, 'transferType', '');
    onTransferFromFavourite(transferType, selectedFavourite);
  };

  const handleFavouriteMenuClick = payload => {
    const option = get(payload, 'option', 'default');
    const transferType = get(payload, 'transferType', '');

    const optionHandler = {
      edit: () => {
        onEditFavourite(payload);
        onFavouriteTypeChange(transferType);
      },
      delete: () => {
        onDeleteFavourite(payload);
        onFavouriteTypeChange(transferType, { isDeleteFavourite: true });
      },
      default: () => null,
    };

    return (optionHandler[option] || optionHandler.default)();
  };

  const radioOptions = isOwnAccountFavouriteDisplay
    ? formatOwnTransferAccounts(receiverAccounts, senderAccountNumber, senderCurrency)
    : formatTransferFavourites(searchableAccounts, searchValue, handleFavouriteMenuClick);

  // to avoid lagging when type in input
  const favouriteSection = useMemo(
    () => (
      <DynoFavouriteSection
        hasSearchSection
        isFormOpen={isFormOpen}
        radioOptions={radioOptions}
        searchValue={searchValue}
        accounts={searchableAccounts}
        onChange={handleTransferFromFavourite}
        transactionType={transactionType}
        onKeywordChange={onKeywordChange}
        label={formatMessage({ id: label })}
        onAddFavourite={handleAddNewFavourite}
        onFavouriteTypeChange={onFavouriteTypeChange}
      />
    ),
    [searchValue, favouriteAccounts, isFormOpen]
  );

  return (
    <>
      {isOwnAccountFavouriteDisplay ? (
        <DynoFavouriteSection
          isFormOpen={isFormOpen}
          radioOptions={radioOptions}
          accounts={receiverAccounts}
          onChange={onOwnAccountChange}
          label={formatMessage({ id: label })}
        />
      ) : (
        favouriteSection
      )}
    </>
  );
};

TransferFavouriteContainer.defaultProps = { onOwnAccountChange: null, receiverAccounts: [], senderAccount: {} };

TransferFavouriteContainer.propTypes = {
  receiverAccounts: PropTypes.array,
  onOwnAccountChange: PropTypes.func,
  senderAccount: PropTypes.object,
  isFormOpen: PropTypes.bool.isRequired,
  searchValue: PropTypes.string.isRequired,
  onAddFavourite: PropTypes.func.isRequired,
  onEditFavourite: PropTypes.func.isRequired,
  onKeywordChange: PropTypes.func.isRequired,
  transactionType: PropTypes.string.isRequired,
  onTransferFromFavourite: PropTypes.func.isRequired,
  onDeleteFavourite: PropTypes.func.isRequired,
  favouriteAccount: PropTypes.object.isRequired,
  onFavouriteTypeChange: PropTypes.func.isRequired,
  isOwnAccountFavouriteDisplay: PropTypes.bool.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default TransferFavouriteContainer;
